/**
 * Utilidades para insertar MathML en CKEditor 5
 * Usa el mismo formato que MathType (imagen con data-mathml) para compatibilidad
 */

import { convertLatexToMathMl as mathliveConvert } from 'mathlive';
import { MathMLToLaTeX } from 'mathml-to-latex';

/**
 * Plugin de CKEditor para registrar data-latex en imágenes
 * Similar a como MathType registra data-mathml
 * Debe incluirse en extraPlugins de cada CKEditor que maneje fórmulas
 */
export function DataLatexPlugin(editor) {
  const schema = editor.model.schema;
  
  if (schema.isRegistered('imageInline')) {
    schema.extend('imageInline', { allowAttributes: ['dataLatex', 'dataMathml'] });
  }
  if (schema.isRegistered('imageBlock')) {
    schema.extend('imageBlock', { allowAttributes: ['dataLatex', 'dataMathml'] });
  }
  
  // --- data-latex ---
  // Upcast: HTML → Model (leer el atributo tal cual)
  editor.conversion.for('upcast').attributeToAttribute({
    view: { name: 'img', key: 'data-latex' },
    model: 'dataLatex'
  });
  
  // Downcast: Model → HTML (escribir el atributo preservando el valor exacto)
  editor.conversion.for('downcast').add(dispatcher => {
    dispatcher.on('attribute:dataLatex:imageInline', (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) return;
      const viewElement = conversionApi.mapper.toViewElement(data.item);
      if (viewElement) {
        conversionApi.writer.setAttribute('data-latex', data.attributeNewValue || '', viewElement);
      }
    });
    dispatcher.on('attribute:dataLatex:imageBlock', (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) return;
      const viewElement = conversionApi.mapper.toViewElement(data.item);
      if (viewElement) {
        const img = viewElement.getChild(0) || viewElement;
        conversionApi.writer.setAttribute('data-latex', data.attributeNewValue || '', img);
      }
    });
  });

  // --- data-mathml ---
  editor.conversion.for('upcast').attributeToAttribute({
    view: { name: 'img', key: 'data-mathml' },
    model: 'dataMathml'
  });
  
  editor.conversion.for('downcast').add(dispatcher => {
    dispatcher.on('attribute:dataMathml:imageInline', (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) return;
      const viewElement = conversionApi.mapper.toViewElement(data.item);
      if (viewElement) {
        conversionApi.writer.setAttribute('data-mathml', data.attributeNewValue || '', viewElement);
      }
    });
    dispatcher.on('attribute:dataMathml:imageBlock', (evt, data, conversionApi) => {
      if (!conversionApi.consumable.consume(data.item, evt.name)) return;
      const viewElement = conversionApi.mapper.toViewElement(data.item);
      if (viewElement) {
        const img = viewElement.getChild(0) || viewElement;
        conversionApi.writer.setAttribute('data-mathml', data.attributeNewValue || '', img);
      }
    });
  });
}

/**
 * Limpia placeholders del LaTeX antes de convertir
 * Los placeholders de MathLive (\placeholder{}) causan problemas de codificación
 * @param {string} latex - LaTeX con posibles placeholders
 * @returns {string} - LaTeX limpio
 */
function cleanLatexPlaceholders(latex) {
    if (!latex) return latex;
    return latex
        .replace(/\\placeholder\{\}/g, '{}')  // Reemplazar con grupo vacío
        .replace(/\\placeholder\{([^}]*)\}/g, '$1')  // Si tiene contenido, mantener solo el contenido
        .replace(/\{\}\s*&/g, '&')  // Limpiar grupos vacíos antes de &
        .replace(/&\s*\{\}/g, '&')  // Limpiar grupos vacíos después de &
        .replace(/\{\}\s*\\\\/g, '\\\\')  // Limpiar grupos vacíos antes de \\
        .replace(/\\\\\s*\{\}/g, '\\\\')  // Limpiar grupos vacíos después de \\
        .replace(/\{\}/g, '');  // Remover grupos vacíos restantes
}

/**
 * Limpia placeholders del MathML
 * MathLive usa principalmente U+25A2 (▢) para placeholders
 * @param {string} mathml - MathML con posibles placeholders
 * @returns {string} - MathML limpio
 */
function cleanMathMLPlaceholders(mathml) {
    if (!mathml) return mathml;
    
    // NO remover <mtd> vacíos porque son necesarios para la alineación
    // En cambio, reemplazar contenido de placeholder con espacio invisible
    return mathml
        // Remover elementos con data-placeholder pero mantener estructura
        .replace(/<[^>]*data-placeholder[^>]*>.*?<\/[^>]+>/gi, '')
        // Reemplazar caracteres de cuadro Unicode con espacio invisible
        .replace(/<mi[^>]*>[\u25A0-\u25FF\u2B1C]<\/mi>/gi, '<mi>&#x200B;</mi>')
        .replace(/<mo[^>]*>[\u25A0-\u25FF\u2B1C]<\/mo>/gi, '')
        .replace(/<mtext[^>]*>[\u25A0-\u25FF\u2B1C]<\/mtext>/gi, '')
        // Remover caracteres de cuadro sueltos
        .replace(/[\u25A0-\u25FF]/g, '')
        .replace(/\u2B1C/g, '')
        // Limpiar mrow vacíos pero NO mtd vacíos
        .replace(/<mrow>\s*<\/mrow>/gi, '')
        .replace(/<mrow\/>/gi, '');
}

/**
 * Wrapper para convertLatexToMathMl que limpia placeholders
 * @param {string} latex - LaTeX a convertir
 * @returns {string} - MathML limpio
 */
function convertLatexToMathMl(latex) {
    const cleanLatex = cleanLatexPlaceholders(latex);
    try {
        const mathml = mathliveConvert(cleanLatex);
        // Verificar que MathLive devolvió MathML válido
        if (!mathml || !mathml.includes('<math')) {
            console.warn('[convertLatexToMathMl] MathLive no devolvió MathML válido para:', cleanLatex.substring(0, 50));
            return null;
        }
        return cleanMathMLPlaceholders(mathml);
    } catch (e) {
        console.warn('[convertLatexToMathMl] Error MathLive:', e.message);
        return null;
    }
}

/**
 * Convierte unidades 'ex' a píxeles
 * 1ex ≈ 8px es el estándar tipográfico (1ex = altura de 'x' minúscula)
 */
const EX_TO_PX_BASE = 8;  // 1ex ≈ 8px (estándar tipográfico)
const BASE_FONT_SIZE = 16; // Tamaño base de MathJax
const PT_TO_PX = 1.333; // 1pt = 1.333px (96dpi / 72dpi)

/**
 * Tamaño mínimo de imagen en px para evitar fórmulas muy pequeñas
 * Valores bajos para mantener proporciones correctas del contenido
 */
const MIN_WIDTH_PX = 10;
const MIN_HEIGHT_PX = 10;

/**
 * Aplica los tamaños mínimos SIN deformar la fórmula.
 *
 * Antes se hacía `Math.max(w, MIN_W)` y `Math.max(h, MIN_H)` por separado, lo
 * que rompía la relación de aspecto en fórmulas pequeñas: una coma de
 * 2.2x6.6px (ar 0.33) terminaba en 10x10px (ar 1.00). Como el viewBox original
 * se conserva, el SVG aplicaba su `preserveAspectRatio` por defecto
 * (xMidYMid meet), encajando el glifo dentro de la caja deformada y rellenando
 * el resto con espacio vacío centrado — el "padding" fantasma alrededor del
 * contenido.
 *
 * Ahora se escala proporcionalmente: si alguna dimensión queda por debajo del
 * mínimo, se multiplican AMBAS por el mismo factor y la fórmula conserva su
 * forma.
 */
const applyMinSizeKeepingAspect = (widthPx, heightPx) => {
    if (!(widthPx > 0) || !(heightPx > 0)) {
        return {
            width: Math.max(widthPx || 0, MIN_WIDTH_PX),
            height: Math.max(heightPx || 0, MIN_HEIGHT_PX),
        };
    }

    const scaleUp = Math.max(
        MIN_WIDTH_PX / widthPx,
        MIN_HEIGHT_PX / heightPx,
        1
    );

    return { width: widthPx * scaleUp, height: heightPx * scaleUp };
};

/**
 * Extrae el tamaño de fuente del MathML si tiene mstyle con mathsize
 * Soporta px y pt como unidades
 * @param {string} mathml 
 * @returns {number} Tamaño en px o 16 por defecto
 */
function extractFontSizeFromMathML(mathml) {
    // Buscar mathsize en px
    const pxMatch = mathml.match(/mathsize=["'](\d+)px["']/i);
    if (pxMatch) {
        return parseInt(pxMatch[1], 10);
    }
    
    // Buscar mathsize en pt (más común en MathType/Wiris)
    const ptMatch = mathml.match(/mathsize=["'](\d+)pt["']/i);
    if (ptMatch) {
        // Convertir pt a px: 12pt = 16px aproximadamente
        return Math.round(parseInt(ptMatch[1], 10) * PT_TO_PX);
    }
    
    return 16; // Tamaño por defecto (16px = 12pt)
}

/**
 * Genera una imagen SVG del MathML usando MathJax
 * @param {string} mathml - Código MathML
 * @returns {Promise<{url: string, width: number, height: number}>} - Data URL y dimensiones
 */
export async function generateMathImage(mathml) {
    return new Promise((resolve) => {
        // Limpiar placeholders del MathML antes de renderizar
        const cleanedMathml = cleanMathMLPlaceholders(mathml);
        
        // Extraer tamaño del MathML si está definido
        const fontSize = extractFontSizeFromMathML(cleanedMathml);
        
        // Crear un contenedor temporal para renderizar
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.fontSize = `${fontSize}px`;
        tempDiv.innerHTML = cleanedMathml;
        document.body.appendChild(tempDiv);

        // Usar MathJax para convertir a SVG
        if (window.MathJax?.tex2svg || window.MathJax?.typesetPromise) {
            window.MathJax.typesetPromise([tempDiv]).then(() => {
                const svg = tempDiv.querySelector('svg');
                if (svg) {
                    // Obtener dimensiones originales en 'ex' y convertir a px
                    let width = svg.getAttribute('width') || '10ex';
                    let height = svg.getAttribute('height') || '3ex';
                    
                    // Convertir ex a px con factor de escala basado en fontSize del MathML
                    // MathJax genera SVG con tamaño base de 16px, escalamos proporcionalmente
                    const scaleFactor = fontSize / BASE_FONT_SIZE;
                    let widthPx = parseFloat(width) * EX_TO_PX_BASE * scaleFactor;
                    let heightPx = parseFloat(height) * EX_TO_PX_BASE * scaleFactor;
                    
                    // Aplicar tamaños mínimos conservando la relación de aspecto
                    ({ width: widthPx, height: heightPx } =
                        applyMinSizeKeepingAspect(widthPx, heightPx));

                    // Clonar y preparar SVG con dimensiones en px para escalado correcto
                    const svgClone = svg.cloneNode(true);
                    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                    
                    // Establecer dimensiones del SVG en px (esto escala el contenido)
                    svgClone.setAttribute('width', `${widthPx}`);
                    svgClone.setAttribute('height', `${heightPx}`);
                    
                    // Mantener viewBox original para que el contenido escale correctamente
                    const originalViewBox = svg.getAttribute('viewBox');
                    if (originalViewBox) {
                        svgClone.setAttribute('viewBox', originalViewBox);
                    }
                    
                    // Obtener el SVG como string
                    const svgString = new XMLSerializer().serializeToString(svgClone);
                    
                    // Usar URL encoding en lugar de base64 (más robusto con caracteres especiales)
                    const encodedSvg = encodeURIComponent(svgString)
                        .replace(/'/g, '%27')
                        .replace(/"/g, '%22');
                    const dataUrl = 'data:image/svg+xml,' + encodedSvg;
                    
                    document.body.removeChild(tempDiv);
                    resolve({ url: dataUrl, width: widthPx, height: heightPx });
                } else {
                    document.body.removeChild(tempDiv);
                    resolve(createPlaceholderSvg(mathml));
                }
            }).catch(() => {
                document.body.removeChild(tempDiv);
                resolve(createPlaceholderSvg(mathml));
            });
        } else {
            document.body.removeChild(tempDiv);
            resolve(createPlaceholderSvg(mathml));
        }
    });
}

/**
 * Crea un SVG placeholder con el texto de la fórmula
 * @returns {{url: string, width: number, height: number}}
 */
function createPlaceholderSvg(mathml) {
    // Extraer texto simple del MathML para el placeholder
    const textContent = mathml.replace(/<[^>]*>/g, '').trim() || '?';
    const width = Math.max(50, textContent.length * 10);
    const height = 30;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <rect width="100%" height="100%" fill="#f0f0f0" stroke="#ccc"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="serif" font-size="14">${textContent}</text>
    </svg>`;
    // Usar URL encoding en lugar de base64
    const encodedSvg = encodeURIComponent(svg);
    return { url: 'data:image/svg+xml,' + encodedSvg, width, height };
}

/**
 * Inserta MathML en una instancia de CKEditor
 * Usa el formato de imagen con data-mathml (compatible con MathType)
 * @param {Object} editor - Instancia de CKEditor
 * @param {string} mathml - Código MathML a insertar
 * @param {string} latex - Código LaTeX (opcional, para data-attribute)
 */
export async function insertMathMLToCKEditor(editor, mathml, latex = '') {
    if (!editor || !mathml) return false;

    try {
        // Generar imagen del MathML con dimensiones
        const { url: imageUrl, width, height } = await generateMathImage(mathml);
        
        // Codificar MathML y LaTeX en Base64 como atributos separados
        const encodedMathML = btoa(unescape(encodeURIComponent(mathml)));
        const encodedLatex = latex ? btoa(unescape(encodeURIComponent(latex))) : '';
        
        // Crear HTML con data-mathml y data-latex como atributos separados
        const formulaHTML = `<img class="nimbus-formula" src="${imageUrl}" data-mathml="${encodedMathML}" data-latex="${encodedLatex}" alt="Fórmula matemática" role="math" align="middle" width="${Math.round(width)}" height="${Math.round(height)}" style="max-width: none; vertical-align: middle;">`;

        // Insertar en CKEditor usando el modelo
        const viewFragment = editor.data.processor.toView(formulaHTML);
        const modelFragment = editor.data.toModel(viewFragment);
        
        editor.model.change(writer => {
            editor.model.insertContent(modelFragment);
        });

        return true;
    } catch (error) {
        console.error('Error insertando MathML en CKEditor:', error);
        return false;
    }
}

/**
 * Reemplaza una fórmula existente en CKEditor
 * @param {Object} editor - Instancia de CKEditor
 * @param {string} mathml - Nuevo código MathML
 * @param {string} latex - Nuevo código LaTeX
 * @param {HTMLElement} elementToReplace - Elemento DOM a reemplazar
 */
export async function replaceMathMLInCKEditor(editor, mathml, latex = '', elementToReplace) {
    if (!editor || !mathml || !elementToReplace) {
        console.warn('replaceMathMLInCKEditor: Datos faltantes');
        return false;
    }

    try {
        
        // Generar nueva imagen del MathML
        const { url: imageUrl, width, height } = await generateMathImage(mathml);
        
        // Codificar MathML y LaTeX en Base64 como atributos separados
        const encodedMathML = btoa(unescape(encodeURIComponent(mathml)));
        const encodedLatex = latex ? btoa(unescape(encodeURIComponent(latex))) : '';
        
        // Crear HTML con data-mathml y data-latex como atributos separados
        const formulaHTML = `<img class="nimbus-formula" src="${imageUrl}" data-mathml="${encodedMathML}" data-latex="${encodedLatex}" alt="Fórmula matemática" role="math" align="middle" width="${Math.round(width)}" height="${Math.round(height)}" style="max-width: none; vertical-align: middle;">`;
        
        // Buscar el elemento en el modelo de CKEditor
        const model = editor.model;
        const root = model.document.getRoot();
        const range = model.createRangeIn(root);
        
        // Obtener el src del elemento a reemplazar para comparar
        const oldSrc = elementToReplace.src;
        const oldDataMathml = elementToReplace.getAttribute('data-mathml');
        
        let imageToReplace = null;
        
        for (const item of range.getItems()) {
            if (item.is('element', 'imageInline') || item.is('element', 'imageBlock')) {
                const src = item.getAttribute('src');
                // Comparar por src
                if (src === oldSrc) {
                    imageToReplace = item;
                    break;
                }
            }
        }
        
        if (imageToReplace) {
            // Reemplazar en el modelo
            model.change(writer => {
                const position = model.createPositionBefore(imageToReplace);
                writer.remove(imageToReplace);
                
                // Insertar nueva imagen
                const viewFragment = editor.data.processor.toView(formulaHTML);
                const modelFragment = editor.data.toModel(viewFragment);
                model.insertContent(modelFragment, position);
            });
            return true;
        } else {
            // Fallback: actualizar DOM directamente y refrescar editor
            elementToReplace.src = imageUrl;
            elementToReplace.setAttribute('data-mathml', encodedMathML);
            elementToReplace.setAttribute('data-latex', encodedLatex);
            elementToReplace.setAttribute('width', Math.round(width));
            elementToReplace.setAttribute('height', Math.round(height));
            
            // Forzar actualización del modelo
            const editorData = editor.getData();
            editor.setData(editorData);
            
            return true;
        }
    } catch (error) {
        console.error('Error reemplazando MathML en CKEditor:', error);
        return false;
    }
}

/**
 * Renderiza fórmulas matemáticas en el editor usando MathJax
 * @param {Object} editor - Instancia de CKEditor
 */
export function renderMathInEditor(editor) {
    try {
        const editorElement = editor.ui?.view?.editable?.element;
        if (!editorElement) return;

        if (window.MathJax?.typesetPromise) {
            // Limpiar typeset anterior para evitar duplicados
            window.MathJax.typesetClear?.([editorElement]);
            window.MathJax.typesetPromise([editorElement]).then(() => {
            }).catch(err => {
                console.warn('MathJax: Error en typeset:', err);
            });
        }
    } catch (error) {
        console.warn('MathJax no disponible para renderizado:', error);
    }
}

/**
 * Decodifica entidades HTML comunes
 * @param {string} str - String con posibles entidades HTML
 * @returns {string} - String decodificado
 */
function decodeHtmlEntities(str) {
    const entities = {
        '&lt;': '<',
        '&gt;': '>',
        '&amp;': '&',
        '&quot;': '"',
        '&#39;': "'",
        '&apos;': "'"
    };
    return str.replace(/&lt;|&gt;|&amp;|&quot;|&#39;|&apos;/gi, match => entities[match]);
}

/**
 * Decodifica MathML desde data-mathml (soporta Base64 y URL encoding)
 * @param {string} encoded - String codificado (Base64 o URL encoded)
 * @returns {string} - MathML decodificado
 */
function decodeMathML(encoded) {
    if (!encoded) return '';
    
    // Intentar decodificar como Base64 primero
    try {
        // Base64 no contiene caracteres como % o <
        if (!encoded.includes('%') && !encoded.includes('<')) {
            const decoded = decodeURIComponent(escape(atob(encoded)));
            if (decoded.includes('<math')) {
                return decoded;
            }
        }
    } catch (e) {
        // No es Base64 válido, continuar con URL decoding
    }
    
    // Intentar decodificar como URL encoding
    try {
        const decoded = decodeURIComponent(encoded);
        return decodeHtmlEntities(decoded);
    } catch (e) {
        console.warn('Error decodificando MathML:', e);
        return encoded;
    }
}

/**
 * Decodifica LaTeX desde data-latex (soporta Base64 y texto plano)
 * @param {string} encoded - String codificado (Base64 o texto plano)
 * @returns {string} - LaTeX decodificado
 */
function decodeLatex(encoded) {
    if (!encoded) return '';
    
    // Intentar nuevo formato JSON (Base64 de JSON con mathml y latex)
    try {
        const decoded = decodeURIComponent(escape(atob(encoded)));
        const data = JSON.parse(decoded);
        if (data.latex) {
            return data.latex;
        }
    } catch (e) {
        // No es JSON, continuar con formatos legacy
    }
    
    // Intentar URL decoding (formato legacy)
    try {
        if (encoded.includes('%')) {
            return decodeURIComponent(encoded);
        }
    } catch (e) {
        // No es URL encoding válido
    }
    
    // Intentar Base64 simple (formato legacy)
    try {
        if (!encoded.includes('\\') && !encoded.includes('^') && !encoded.includes('_')) {
            const decoded = decodeURIComponent(escape(atob(encoded)));
            if (decoded.includes('\\') || decoded.includes('^') || decoded.includes('_') || /^[a-zA-Z0-9\s\+\-\*\/\=\(\)\[\]\{\}]+$/.test(decoded)) {
                return decoded;
            }
        }
    } catch (e) {
        // No es Base64 válido
    }
    
    // Retornar tal cual si no se puede decodificar
    return encoded;
}

/**
 * Limpia completamente el HTML de elementos de UI de CKEditor
 * Remueve resizers, handles, selection handles, type-around, etc.
 * @param {string} html - HTML con elementos de CKEditor
 * @returns {string} - HTML limpio para guardar
 */
export function cleanCKEditorHTML(html) {
    if (!html || typeof html !== 'string') return html;
    
    // Usar DOMParser para una limpieza más precisa de estructuras anidadas
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div id="ck-clean-root">${html}</div>`, 'text/html');
        const root = doc.getElementById('ck-clean-root');
        
        if (!root) return html;
        
        // 1. Remover elementos de UI de CKEditor por selectores
        const selectorsToRemove = [
            '.ck-widget__resizer',
            '.ck-widget__selection-handle', 
            '.ck-widget__type-around',
            '.ck-table-column-resizer',
            '.ck-size-view',
            '.ck-reset_all',
            '[class*="ck-widget__resizer"]',
            '[class*="ck-widget__type-around"]'
        ];
        
        selectorsToRemove.forEach(selector => {
            try {
                const elements = root.querySelectorAll(selector);
                elements.forEach(el => el.remove());
            } catch (e) {
                // Selector inválido, ignorar
            }
        });
        
        // 2. Remover spans de ck-table-bogus-paragraph pero preservar contenido
        const bogusSpans = root.querySelectorAll('.ck-table-bogus-paragraph');
        bogusSpans.forEach(span => {
            const parent = span.parentNode;
            while (span.firstChild) {
                parent.insertBefore(span.firstChild, span);
            }
            span.remove();
        });

        // Remover saltos de relleno generados por CKEditor. Cuando el
        // relleno duplica un div vacío original dentro de una celda, se quita
        // únicamente el contenedor artificial y se conserva el div original.
        const fillerBreaks = Array.from(
            root.querySelectorAll('br[data-cke-filler]')
        );
        fillerBreaks.forEach(fillerBreak => {
            const parent = fillerBreak.parentElement;
            const sibling = parent && parent.nextElementSibling;
            const parentAttributes = parent
                ? Array.from(parent.attributes).filter(attribute => {
                    if (attribute.name === 'class') {
                        return attribute.value
                            .split(/\s+/)
                            .some(className =>
                                className &&
                                !className.startsWith('ck-')
                            );
                    }

                    return !attribute.name.startsWith('data-cke-');
                })
                : [];
            const isOnlyContent =
                parent &&
                parent.localName === 'div' &&
                parent.children.length === 1 &&
                parent.firstElementChild === fillerBreak &&
                !parent.textContent.trim();
            const hasEmptyDivSibling =
                sibling &&
                sibling.localName === 'div' &&
                !sibling.textContent.trim() &&
                !sibling.querySelector('img, math, svg, table');

            if (
                isOnlyContent &&
                !parentAttributes.length &&
                parent.closest('td, th') &&
                hasEmptyDivSibling
            ) {
                parent.remove();
            } else {
                fillerBreak.remove();
            }
        });
        
        // 3. Limpiar atributos de todos los elementos
        const allElements = root.querySelectorAll('*');
        allElements.forEach(el => {
            // Remover contenteditable
            el.removeAttribute('contenteditable');
            // Remover role="textbox"
            if (el.getAttribute('role') === 'textbox') {
                el.removeAttribute('role');
            }
            Array.from(el.attributes).forEach(attribute => {
                if (attribute.name.startsWith('data-cke-')) {
                    el.removeAttribute(attribute.name);
                }
            });
            // Limpiar clases ck-*
            if (el.hasAttribute('class')) {
                const cleanClasses = (el.getAttribute('class') || '')
                    .split(/\s+/)
                    .filter(c => !c.startsWith('ck-') && c !== 'image-inline' && c.trim() !== '')
                    .join(' ')
                    .trim();
                if (cleanClasses) {
                    el.className = cleanClasses;
                } else {
                    el.removeAttribute('class');
                }
            }
            if (el.hasAttribute('style') && !el.getAttribute('style').trim()) {
                el.removeAttribute('style');
            }
        });
        
        // 4. Preservar divs vacíos: pueden ser elementos visuales intencionales
        // (separadores, barras, degradados o espaciadores definidos por CSS).
        // Los elementos de UI ya fueron removidos por los selectores anteriores.

        // 5. Limpiar espacios/saltos de línea excesivos en los nodos de texto,
        // preservando el contenido de bloques <pre>/<code> (p.ej. código Python
        // pegado en un CodeBlock) y <style>, donde los espacios consecutivos
        // son significativos y no espacio en blanco decorativo.
        const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        let textNode;
        while ((textNode = walker.nextNode())) {
            if (!textNode.parentElement?.closest('pre, code, style')) {
                textNodes.push(textNode);
            }
        }
        textNodes.forEach((node) => {
            node.textContent = node.textContent
                .replace(/\s{2,}/g, ' ')
                .replace(/(\r?\n){3,}/g, '\n\n');
        });

        // Obtener el HTML limpio
        const result = root.innerHTML;

        return result.trim();
    } catch (e) {
        // Fallback: usar regex si DOMParser falla
        console.warn('[cleanCKEditorHTML] DOMParser falló, usando regex fallback:', e);
        return cleanCKEditorHTMLRegex(html);
    }
}

/**
 * Fallback con regex para limpiar HTML de CKEditor
 * Se usa cuando DOMParser no está disponible
 */
function cleanCKEditorHTMLRegex(html) {
    if (!html || typeof html !== 'string') return html;
    
    let result = html;
    
    // Remover elementos de UI de CKEditor usando regex
    // Aplicar múltiples veces para manejar anidamiento
    for (let i = 0; i < 5; i++) {
        const prevLength = result.length;
        
        // Remover divs con clases de CKEditor UI
        result = result.replace(/<div[^>]*class="[^"]*(?:ck-widget__resizer|ck-widget__selection-handle|ck-widget__type-around|ck-table-column-resizer|ck-size-view|ck-reset_all)[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
        
        // Si no hubo cambios, salir del loop
        if (result.length === prevLength) break;
    }
    
    // Remover spans de ck-table-bogus-paragraph pero preservar contenido
    result = result.replace(/<span[^>]*class="[^"]*ck-table-bogus-paragraph[^"]*"[^>]*>([\s\S]*?)<\/span>/gi, '$1');
    
    // Remover atributos contenteditable
    result = result.replace(/\s+contenteditable="[^"]*"/gi, '');
    
    // Remover atributos role="textbox"
    result = result.replace(/\s+role="textbox"/gi, '');
    
    // Limpiar clases ck-* de todos los elementos
    result = result.replace(/\s+class="([^"]*)"/gi, (match, classes) => {
        const cleanClasses = classes
            .split(/\s+/)
            .filter(c => !c.startsWith('ck-') && c !== 'image-inline' && c.trim() !== '')
            .join(' ')
            .trim();
        return cleanClasses ? ` class="${cleanClasses}"` : '';
    });
    
    // Limpiar divs vacíos
    result = result.replace(/<div[^>]*>\s*<\/div>/gi, '');

    // Limpiar múltiples espacios en blanco, preservando el contenido de
    // bloques <pre> (p.ej. código Python pegado en un CodeBlock), donde los
    // espacios consecutivos son la indentación y no espacio decorativo.
    const preSegments = [];
    result = result.replace(/<pre[\s\S]*?<\/pre>/gi, (match) => {
        preSegments.push(match);
        return ` PRE_BLOCK_${preSegments.length - 1} `;
    });

    result = result.replace(/\s{2,}/g, ' ');

    // Limpiar saltos de línea excesivos
    result = result.replace(/(\r?\n){3,}/g, '\n\n');

    result = result.replace(/ PRE_BLOCK_(\d+) /g, (_, idx) => preSegments[Number(idx)]);

    return result.trim();
}

/**
 * Limpia las estructuras de widget de CKEditor que envuelven las fórmulas
 * Esto previene que se acumulen wrappers al guardar múltiples veces
 * @param {string} html - HTML con posibles wrappers de CKEditor
 * @returns {string} - HTML limpio sin wrappers de CKEditor
 */
function cleanCKEditorWidgetWrappers(html) {
    if (!html || typeof html !== 'string') return html;
    
    // Primero aplicar limpieza general de CKEditor
    let result = cleanCKEditorHTML(html);
    
    // Remover spans con clases de widget de CKEditor que envuelven las fórmulas
    // Patrón: <span class="image-inline ck-widget..."><span class="nimbus-formula-wrapper">...<img>...</span></span>
    // Queremos extraer solo: <span class="nimbus-formula-wrapper">...<img>...</span>
    
    // 1. Remover spans de widget de CKEditor pero preservar el contenido interno
    // Buscar spans con clases ck-widget que contienen nimbus-formula
    result = result.replace(
        /<span[^>]*class="[^"]*(?:image-inline|ck-widget)[^"]*"[^>]*>([\s\S]*?<img[^>]*class="[^"]*nimbus-formula[^"]*"[^>]*>[\s\S]*?)<\/span>/gi,
        (match, innerContent) => {
            // Si el contenido interno ya tiene el wrapper nimbus-formula-wrapper, extraerlo
            const wrapperMatch = innerContent.match(/<span[^>]*class="[^"]*nimbus-formula-wrapper[^"]*"[^>]*>[\s\S]*?<\/span>/i);
            if (wrapperMatch) {
                return wrapperMatch[0];
            }
            // Si no tiene wrapper, buscar solo la imagen
            const imgMatch = innerContent.match(/<img[^>]*class="[^"]*nimbus-formula[^"]*"[^>]*>/i);
            if (imgMatch) {
                return `<span class="nimbus-formula-wrapper" style="display:inline;">${imgMatch[0]}</span>`;
            }
            return match;
        }
    );
    
    // 2. Limpiar clases de CKEditor de las imágenes de fórmulas
    result = result.replace(
        /<img([^>]*class=")([^"]*nimbus-formula[^"]*)("[^>]*>)/gi,
        (match, before, classes, after) => {
            // Remover clases de CKEditor, mantener solo nimbus-formula
            const cleanClasses = classes
                .split(/\s+/)
                .filter(c => !c.startsWith('ck-') && c !== 'image-inline')
                .join(' ');
            return `<img${before}${cleanClasses}${after}`;
        }
    );
    
    // 3. Remover &nbsp; sueltos que CKEditor agrega
    result = result.replace(/&nbsp;(<span class="nimbus-formula-wrapper)/gi, '$1');
    result = result.replace(/(<\/span>)&nbsp;/gi, '$1');
    
    return result;
}

/**
 * Convierte imágenes de fórmulas con SVG base64 a MathML puro para visualización
 * Prioriza usar data-latex con convertLatexToMathMl para generar MathML limpio
 * Si no hay data-latex, usa data-mathml como fallback
 * @param {string} html - HTML con imágenes de fórmulas (SVG base64 + data-mathml/data-latex)
 * @returns {string} - HTML con MathML puro en lugar de imágenes
 */
export function convertFormulaImagesToMathML(html) {
    if (!html || typeof html !== 'string') return html;
    
    let result = html;
    
    // 0. Limpiar estructuras de widget de CKEditor que envuelven las fórmulas
    // Esto previene que se acumulen wrappers al guardar múltiples veces
    result = cleanCKEditorWidgetWrappers(result);
    
    // 1. Buscar todas las imágenes nimbus-formula y convertirlas a MathML
    // Soporta nuevo formato JSON en data-mathml y formato legacy con data-latex separado
    const imgRegex = /<img[^>]*class="[^"]*nimbus-formula[^"]*"[^>]*>/gi;
    
    result = result.replace(imgRegex, (match) => {
        try {
            // Verificar si la imagen tiene un src válido (SVG data URL)
            const srcMatch = match.match(/src=["']([^"']+)["']/i);
            const hasSvgDataUrl = srcMatch && srcMatch[1] && srcMatch[1].startsWith('data:image/svg+xml');
            
            // Extraer LaTeX original (se preservará en el <math> tag)
            let originalLatex = '';
            const latexMatch = match.match(/data-latex=["']([^"']+)["']/i);
            if (latexMatch && latexMatch[1]) {
                originalLatex = decodeLatex(latexMatch[1]);
            }
            
            // Helper: inyectar data-latex en el <math> tag para preservar el LaTeX original
            const injectLatexInMathML = (mathml, latex) => {
                if (!latex || !mathml) return mathml;
                const encodedLatex = btoa(unescape(encodeURIComponent(latex)));
                // Inyectar como atributo data-original-latex en el <math> tag
                return mathml.replace(/<math/, `<math data-original-latex="${encodedLatex}"`);
            };
            
            // 1. PRIORIDAD: usar data-mathml directamente (ya contiene MathML válido en Base64)
            const mathmlMatch = match.match(/data-mathml=["']([^"']+)["']/i);
            if (mathmlMatch && mathmlMatch[1] && mathmlMatch[1].length > 10) {
                try {
                    // Intentar decodificar como Base64 directo (formato más común)
                    const mathml = decodeMathML(mathmlMatch[1]);
                    if (mathml && mathml.includes('<math')) {
                        return injectLatexInMathML(mathml, originalLatex);
                    }
                } catch (e) {
                    console.warn('[convertFormulaImagesToMathML] Error decodificando data-mathml:', e);
                }
            }
            
            // 2. Fallback: convertir data-latex a MathML con MathLive
            if (originalLatex && originalLatex.trim()) {
                try {
                    const mathml = convertLatexToMathMl(originalLatex);
                    if (mathml && mathml.includes('<math')) {
                        return injectLatexInMathML(mathml, originalLatex);
                    }
                } catch (mathLiveError) {
                    console.warn('[convertFormulaImagesToMathML] Error MathLive:', mathLiveError.message);
                }
            }
            
            // 3. Último fallback: si tenemos LaTeX original y data-mathml,
            //    usar data-mathml decodificado directamente (no requiere conversión perfecta)
            if (mathmlMatch && mathmlMatch[1]) {
                const mathml = decodeMathML(mathmlMatch[1]);
                if (mathml && mathml.includes('<math')) {
                    return injectLatexInMathML(mathml, originalLatex);
                }
            }
            
            // Si no se pudo convertir a MathML, mantener la imagen original
            return match;
        } catch (e) {
            console.warn('Error convirtiendo fórmula a MathML:', e);
            return match;
        }
    });
    
    // 2. Buscar otras imágenes con data-mathml (ej: Wirisformula) y convertirlas
    const otherImgRegex = /<img[^>]*data-mathml=["']([^"']+)["'][^>]*>/gi;
    
    result = result.replace(otherImgRegex, (match, encodedMathML) => {
        // Si ya fue procesada (convertida a <math>), no procesar de nuevo
        if (match.includes('<math')) return match;
        
        try {
            const mathml = decodeMathML(encodedMathML);
            if (mathml && mathml.includes('<math')) {
                return mathml;
            }
            return match;
        } catch (e) {
            console.warn('Error decodificando MathML:', e);
            return match;
        }
    });
    
    // 3. Buscar MathML escapado como texto (ej: &lt;math...&gt;) y convertirlo a HTML real
    const escapedMathRegex = /&lt;math[^&]*?&gt;[\s\S]*?&lt;\/math&gt;/gi;

    result = result.replace(escapedMathRegex, (match) => {
        try {
            return decodeHtmlEntities(match);
        } catch (e) {
            console.warn('Error decodificando MathML escapado:', e);
            return match;
        }
    });

    // 4. CKEditor omite border-width cuando coincide con el defaultProperties (1px).
    //    Si hay border-color en el <table> pero no border-width, inyectar border-width:1px.
    result = result.replace(
        /<table([^>]*style="([^"]*)")([^>]*>)/gi,
        (match, attrsBefore, styleContent, attrsAfter) => {
            if (styleContent.includes('border-color') && !styleContent.includes('border-width')) {
                const newStyle = styleContent.trimEnd().replace(/;+$/, '') + ';border-width:1px';
                return `<table${attrsBefore.replace(styleContent, newStyle)}${attrsAfter}`;
            }
            return match;
        }
    );

    return result;
}

/**
 * Normaliza dimensiones de imágenes de fórmulas que tengan unidades 'ex' a 'px'
 * @param {string} html - HTML con posibles imágenes con dimensiones en ex
 * @returns {string} - HTML con dimensiones normalizadas a px
 */
export function normalizeFormulaImageDimensions(html) {
    if (!html || typeof html !== 'string') return html;
    
    // Convertir width="Xex" a width="Ypx"
    let result = html.replace(
        /width=["']([\d.]+)ex["']/gi,
        (match, value) => {
            const pxValue = Math.max(Math.round(parseFloat(value) * EX_TO_PX_BASE), MIN_WIDTH_PX);
            return `width="${pxValue}"`;
        }
    );
    
    // Convertir height="Xex" a height="Ypx"
    result = result.replace(
        /height=["']([\d.]+)ex["']/gi,
        (match, value) => {
            const pxValue = Math.max(Math.round(parseFloat(value) * EX_TO_PX_BASE), MIN_HEIGHT_PX);
            return `height="${pxValue}"`;
        }
    );
    
    return result;
}

/**
 * Preprocesa HTML para convertir MathML inline a formato de imagen
 * Esto es necesario porque CKEditor no puede manejar MathML inline en contenteditable
 * @param {string} html - HTML con posible MathML inline
 * @returns {Promise<string>} - HTML con MathML convertido a imágenes
 */
export async function preprocessMathMLForCKEditor(html) {
    if (!html || typeof html !== 'string') return html;
    
    // Primero limpiar estructuras de widget de CKEditor que pueden haberse guardado
    let processedHtml = cleanCKEditorWidgetWrappers(html);
    
    // Normalizar imágenes existentes con dimensiones en 'ex'
    processedHtml = normalizeFormulaImageDimensions(processedHtml);
    
    // Regex para encontrar etiquetas <math>...</math>
    const mathRegex = /<math[^>]*>[\s\S]*?<\/math>/gi;
    const matches = processedHtml.match(mathRegex);
    
    if (!matches || matches.length === 0) return processedHtml;
    
    for (const mathml of matches) {
        try {
            // Generar imagen del MathML con dimensiones
            const { url: imageUrl, width, height } = await generateMathImageFromMathML(mathml);
            const encodedMathML = btoa(unescape(encodeURIComponent(mathml)));
            
            // Intentar recuperar el LaTeX ORIGINAL del atributo data-original-latex del <math> tag
            // Esto evita la reconversión lossy MathML → LaTeX con MathMLToLaTeX
            let encodedLatex = '';
            const originalLatexMatch = mathml.match(/data-original-latex="([^"]+)"/);
            if (originalLatexMatch && originalLatexMatch[1]) {
                // El LaTeX ya viene en Base64, usarlo directamente
                encodedLatex = originalLatexMatch[1];
            } else {
                // Fallback: intentar convertir MathML a LaTeX (puede perder info en matrices)
                try {
                    let latex = MathMLToLaTeX.convert(mathml);
                    latex = latex.replace(/^\$\$\s*/g, '').replace(/\s*\$\$$/g, '').trim();
                    if (latex) {
                        encodedLatex = btoa(unescape(encodeURIComponent(latex)));
                    }
                } catch (e) {
                    // Si falla la conversión, data-latex queda vacío
                }
            }
            
            // Crear img con data-mathml y data-latex en Base64
            const imgTag = `<img class="nimbus-formula" src="${imageUrl}" data-mathml="${encodedMathML}" data-latex="${encodedLatex}" alt="Fórmula matemática" role="math" align="middle" width="${Math.round(width)}" height="${Math.round(height)}" style="max-width: none; vertical-align: middle;">`;
            
            // Reemplazar el MathML con la imagen
            processedHtml = processedHtml.replace(mathml, imgTag);
        } catch (error) {
            console.warn('Error procesando MathML:', error);
        }
    }
    
    return processedHtml;
}

/**
 * Genera una imagen SVG del MathML usando MathJax (versión para preprocesamiento)
 * @returns {Promise<{url: string, width: number, height: number}>}
 */
async function generateMathImageFromMathML(mathml) {
    return new Promise((resolve) => {
        // Limpiar placeholders del MathML antes de renderizar
        const cleanedMathml = cleanMathMLPlaceholders(mathml);
        
        // Extraer tamaño del MathML si está definido
        const fontSize = extractFontSizeFromMathML(cleanedMathml);
        const scaleFactor = fontSize / 16;
        
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.fontSize = `${fontSize}px`;
        tempDiv.innerHTML = cleanedMathml;
        document.body.appendChild(tempDiv);

        if (window.MathJax?.typesetPromise) {
            window.MathJax.typesetPromise([tempDiv]).then(() => {
                const svg = tempDiv.querySelector('svg');
                if (svg) {
                    // Obtener dimensiones y convertir ex a px con escala
                    let width = svg.getAttribute('width') || '10ex';
                    let height = svg.getAttribute('height') || '3ex';
                    let widthPx = parseFloat(width) * EX_TO_PX_BASE * scaleFactor;
                    let heightPx = parseFloat(height) * EX_TO_PX_BASE * scaleFactor;
                        
                    // Aplicar tamaños mínimos conservando la relación de aspecto
                    ({ width: widthPx, height: heightPx } =
                        applyMinSizeKeepingAspect(widthPx, heightPx));

                    // Clonar y preparar SVG con dimensiones en px
                    const svgClone = svg.cloneNode(true);
                    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                    svgClone.setAttribute('width', `${widthPx}`);
                    svgClone.setAttribute('height', `${heightPx}`);
                        
                    // Mantener viewBox original para escalado correcto
                    const originalViewBox = svg.getAttribute('viewBox');
                    if (originalViewBox) {
                        svgClone.setAttribute('viewBox', originalViewBox);
                    }
                        
                    const svgString = new XMLSerializer().serializeToString(svgClone);
                        
                    // Usar URL encoding en lugar de base64
                    const encodedSvg = encodeURIComponent(svgString)
                        .replace(/'/g, '%27')
                        .replace(/"/g, '%22');
                    const dataUrl = 'data:image/svg+xml,' + encodedSvg;
                    
                    document.body.removeChild(tempDiv);
                    resolve({ url: dataUrl, width: widthPx, height: heightPx });
                } else {
                    document.body.removeChild(tempDiv);
                    resolve(createFallbackPlaceholder(mathml));
                }
            }).catch(() => {
                document.body.removeChild(tempDiv);
                resolve(createFallbackPlaceholder(mathml));
            });
        } else {
            document.body.removeChild(tempDiv);
            resolve(createFallbackPlaceholder(mathml));
        }
    });
}

/**
 * Crea un placeholder SVG simple
 * @returns {{url: string, width: number, height: number}}
 */
function createFallbackPlaceholder(mathml) {
    const textContent = mathml.replace(/<[^>]*>/g, '').trim() || '?';
    const width = Math.max(50, textContent.length * 8);
    const height = 24;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="serif" font-size="14">${textContent}</text></svg>`;
    // Usar URL encoding en lugar de base64
    const encodedSvg = encodeURIComponent(svg);
    return { url: 'data:image/svg+xml,' + encodedSvg, width, height };
}

/**
 * Extrae MathML de un elemento de fórmula
 * Prioriza usar data-latex con convertLatexToMathMl para generar MathML limpio
 * Si no hay data-latex, usa data-mathml como fallback
 * @param {HTMLElement} element - Elemento con data-mathml o data-latex
 * @returns {string} MathML decodificado o generado desde LaTeX
 */
export function extractMathMLFromElement(element) {
    if (!element) return '';
    
    const encodedData = element.getAttribute('data-mathml');
    if (!encodedData) return '';
    
    // Intentar nuevo formato JSON (contiene mathml y latex)
    try {
        const decoded = decodeURIComponent(escape(atob(encodedData)));
        const data = JSON.parse(decoded);
        // Si es JSON con mathml, retornar el mathml
        if (data.mathml) {
            return data.mathml;
        }
    } catch (e) {
        // No es JSON, es formato legacy
    }
    
    // Formato legacy: data-mathml contiene solo MathML
    // Primero intentar data-latex para generar MathML limpio
    const encodedLatex = element.getAttribute('data-latex');
    if (encodedLatex) {
        try {
            const latex = decodeLatex(encodedLatex);
            if (latex && latex.trim()) {
                const mathml = convertLatexToMathMl(latex);
                if (mathml && mathml.includes('<math')) {
                    return mathml;
                }
            }
        } catch (e) {
            console.warn('Error convirtiendo LaTeX a MathML:', e);
        }
    }
    
    // Fallback: decodificar data-mathml legacy
    return decodeMathML(encodedData);
}

/**
 * Extrae LaTeX de un elemento de fórmula
 * Soporta el nuevo formato JSON en data-mathml y el formato legacy data-latex
 * @param {HTMLElement} element - Elemento con data-mathml (JSON) o data-latex
 * @returns {string} LaTeX decodificado
 */
export function extractLatexFromElement(element) {
    if (!element) {
        return '';
    }
    
    // 1. Prioridad: data-latex como atributo separado
    const encodedLatex = element.getAttribute('data-latex');
    if (encodedLatex) {
        // Intentar Base64
        try {
            const decoded = decodeURIComponent(escape(atob(encodedLatex)));
            if (decoded) return decoded;
        } catch (e) {
            // No es Base64 válido
        }
        // Intentar URL decoding
        try {
            if (encodedLatex.includes('%')) {
                return decodeURIComponent(encodedLatex);
            }
        } catch (e) {
            // No es URL encoding válido
        }
        return encodedLatex;
    }
    
    // 2. Fallback: intentar formato JSON legacy en data-mathml
    const encodedMathml = element.getAttribute('data-mathml');
    if (encodedMathml) {
        try {
            const decoded = decodeURIComponent(escape(atob(encodedMathml)));
            const data = JSON.parse(decoded);
            if (data.latex) {
                return data.latex;
            }
        } catch (e) {
            // No es JSON, no tiene latex embebido
        }
    }
    
    return '';
}

/**
 * Extrae los estilos guardados de un elemento de fórmula
 * Soporta tanto el formato antiguo (JSON en data-mathml) como el nuevo (MathML puro)
 * @param {HTMLElement} element - Elemento con data-mathml
 * @returns {Object} Estilos { color, size, font }
 */
export function extractStylesFromElement(element) {
    if (!element) return { color: '', size: '', font: '' };
    
    const encodedMathml = element.getAttribute('data-mathml');
    if (encodedMathml) {
        try {
            const decoded = decodeURIComponent(escape(atob(encodedMathml)));
            
            // Intentar parsear como JSON (formato antiguo)
            if (decoded.startsWith('{')) {
                const data = JSON.parse(decoded);
                if (data.styles) {
                    return {
                        color: data.styles.color || '',
                        size: data.styles.size || '',
                        font: data.styles.font || ''
                    };
                }
            }
            
            // Formato nuevo: MathML puro - extraer estilos de mstyle
            if (decoded.includes('<math')) {
                const styles = { color: '', size: '', font: '' };
                
                // Extraer mathcolor
                const colorMatch = decoded.match(/mathcolor=["']([^"']+)["']/i);
                if (colorMatch) styles.color = colorMatch[1];
                
                // Extraer mathsize
                const sizeMatch = decoded.match(/mathsize=["']([^"']+)["']/i);
                if (sizeMatch) styles.size = sizeMatch[1];
                
                // Extraer mathvariant (font)
                const fontMatch = decoded.match(/mathvariant=["']([^"']+)["']/i);
                if (fontMatch) styles.font = fontMatch[1];
                
                return styles;
            }
        } catch (e) {
            // Error decodificando
        }
    }
    
    return { color: '', size: '', font: '' };
}

/**
 * Copia el MathML de una fórmula al portapapeles
 * @param {HTMLElement} element - Elemento img con data-mathml
 * @returns {Promise<boolean>} - true si se copió exitosamente
 */
export async function copyMathMLToClipboard(element) {
    const mathml = extractMathMLFromElement(element);
    if (!mathml) {
        console.warn('No se encontró MathML en el elemento');
        return false;
    }
    
    try {
        await navigator.clipboard.writeText(mathml);
        return true;
    } catch (e) {
        console.error('Error copiando MathML:', e);
        // Fallback para navegadores sin soporte de clipboard API
        const textarea = document.createElement('textarea');
        textarea.value = mathml;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(textarea);
            return true;
        } catch (e2) {
            document.body.removeChild(textarea);
            return false;
        }
    }
}

/**
 * Obtiene el MathML formateado de una fórmula (para mostrar o copiar)
 * @param {HTMLElement} element - Elemento img con data-mathml
 * @returns {string} MathML formateado con indentación
 */
export function getFormattedMathML(element) {
    const mathml = extractMathMLFromElement(element);
    if (!mathml) return '';
    
    // Formatear con indentación básica
    try {
        const formatted = mathml
            .replace(/></g, '>\n<')
            .replace(/(<[^/][^>]*>)\n(<[^/])/g, '$1\n  $2');
        return formatted;
    } catch {
        return mathml;
    }
}

/**
 * Muestra un toast de confirmación
 * @param {string} message - Mensaje a mostrar
 */
function showToast(message) {
    const existing = document.querySelector('.nimbus-formula-copied-toast');
    if (existing) existing.remove();
    
    const toast = document.createElement('div');
    toast.className = 'nimbus-formula-copied-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.remove(), 2000);
}

/**
 * Copia el LaTeX de una fórmula al portapapeles
 * @param {HTMLElement} element - Elemento img con data-latex o data-mathml
 * @returns {Promise<boolean>} - true si se copió exitosamente
 */
export async function copyLatexToClipboard(element) {
    const latex = extractLatexFromElement(element);
    if (!latex) {
        console.warn('No se encontró LaTeX en el elemento');
        return false;
    }
    
    try {
        await navigator.clipboard.writeText(latex);
        return true;
    } catch (e) {
        console.error('Error copiando LaTeX:', e);
        const textarea = document.createElement('textarea');
        textarea.value = latex;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(textarea);
            return true;
        } catch (e2) {
            document.body.removeChild(textarea);
            return false;
        }
    }
}

/**
 * Convierte la imagen SVG de una fórmula a un Blob PNG
 * @param {HTMLElement} imgElement - Elemento img con src SVG (data URL)
 * @param {number} scale - Factor de escala para mejor resolución (default 2)
 * @returns {Promise<Blob>} - Blob PNG de la imagen
 */
function formulaImageToPngBlob(imgElement, scale = 2) {
    return new Promise((resolve, reject) => {
        // Resolver el elemento <img> real (puede ser un wrapper como .nimbus-formula)
        let targetImg = imgElement;
        if (targetImg.tagName !== 'IMG') {
            targetImg = targetImg.querySelector('img') || targetImg;
        }
        const src = targetImg.getAttribute('src') || targetImg.src;
        if (!src) {
            reject(new Error('No se encontró src en el elemento'));
            return;
        }
        
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const width = img.naturalWidth || targetImg.width || 100;
            const height = img.naturalHeight || targetImg.height || 40;
            canvas.width = width * scale;
            canvas.height = height * scale;
            
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Error generando PNG blob'));
                }
            }, 'image/png');
        };
        img.onerror = () => reject(new Error('Error cargando imagen de fórmula'));
        img.src = src;
    });
}

/**
 * Copia la imagen de una fórmula al portapapeles como PNG
 * @param {HTMLElement} imgElement - Elemento img de la fórmula
 * @returns {Promise<boolean>} - true si se copió exitosamente
 */
export async function copyFormulaImageToClipboard(imgElement) {
    try {
        const blob = await formulaImageToPngBlob(imgElement);
        await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
        ]);
        return true;
    } catch (e) {
        console.error('Error copiando imagen al portapapeles:', e);
        return false;
    }
}

/**
 * Descarga la imagen de una fórmula como archivo PNG
 * @param {HTMLElement} imgElement - Elemento img de la fórmula
 * @param {string} filename - Nombre del archivo (default: 'formula.png')
 * @returns {Promise<boolean>} - true si se descargó exitosamente
 */
export async function downloadFormulaImage(imgElement, filename = 'formula.png') {
    try {
        const blob = await formulaImageToPngBlob(imgElement);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return true;
    } catch (e) {
        console.error('Error descargando imagen:', e);
        return false;
    }
}

/**
 * Crea y muestra el menú contextual para una fórmula
 * @param {MouseEvent} event - Evento de clic derecho
 * @param {HTMLElement} formulaElement - Elemento de la fórmula
 * @param {Function} onEdit - Callback para editar la fórmula (opcional)
 */
function showFormulaContextMenu(event, formulaElement, onEdit) {
    event.preventDefault();
    
    // Remover menú existente
    const existing = document.querySelector('.nimbus-formula-context-menu');
    if (existing) existing.remove();
    
    const menu = document.createElement('div');
    menu.className = 'nimbus-formula-context-menu';
    
    // Opción: Copiar MathML
    const copyMathMLItem = document.createElement('div');
    copyMathMLItem.className = 'nimbus-formula-context-menu-item';
    copyMathMLItem.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
        <span>Copiar MathML</span>
    `;
    copyMathMLItem.addEventListener('click', async () => {
        const success = await copyMathMLToClipboard(formulaElement);
        menu.remove();
        if (success) {
            showToast('MathML copiado al portapapeles');
        } else {
            showToast('Error al copiar MathML');
        }
    });
    menu.appendChild(copyMathMLItem);
    
    // Opción: Copiar LaTeX
    const latexValue = extractLatexFromElement(formulaElement);
    if (latexValue) {
        const copyLatexItem = document.createElement('div');
        copyLatexItem.className = 'nimbus-formula-context-menu-item';
        copyLatexItem.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"></path>
                <path d="M9 15l2-2 2 2"></path>
                <path d="M11 13v4"></path>
            </svg>
            <span>Copiar LaTeX</span>
        `;
        copyLatexItem.addEventListener('click', async () => {
            const success = await copyLatexToClipboard(formulaElement);
            menu.remove();
            if (success) {
                showToast('LaTeX copiado al portapapeles');
            } else {
                showToast('Error al copiar LaTeX');
            }
        });
        menu.appendChild(copyLatexItem);
    }
    
    // Separador antes de opciones de imagen
    const imgDivider = document.createElement('div');
    imgDivider.className = 'nimbus-formula-context-menu-divider';
    menu.appendChild(imgDivider);
    
    // Opción: Copiar Imagen
    const copyImageItem = document.createElement('div');
    copyImageItem.className = 'nimbus-formula-context-menu-item';
    copyImageItem.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
        <span>Copiar imagen</span>
    `;
    copyImageItem.addEventListener('click', async () => {
        menu.remove();
        const success = await copyFormulaImageToClipboard(formulaElement);
        if (success) {
            showToast('Imagen copiada al portapapeles');
        } else {
            showToast('Error al copiar imagen');
        }
    });
    menu.appendChild(copyImageItem);
    
    // Opción: Descargar Imagen
    const downloadImageItem = document.createElement('div');
    downloadImageItem.className = 'nimbus-formula-context-menu-item';
    downloadImageItem.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span>Descargar imagen</span>
    `;
    downloadImageItem.addEventListener('click', async () => {
        menu.remove();
        const success = await downloadFormulaImage(formulaElement);
        if (success) {
            showToast('Imagen descargada');
        } else {
            showToast('Error al descargar imagen');
        }
    });
    menu.appendChild(downloadImageItem);
    
    // Opción: Editar fórmula (si hay callback)
    if (onEdit && typeof onEdit === 'function') {
        const divider = document.createElement('div');
        divider.className = 'nimbus-formula-context-menu-divider';
        menu.appendChild(divider);
        
        const editItem = document.createElement('div');
        editItem.className = 'nimbus-formula-context-menu-item';
        editItem.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            <span>Editar fórmula</span>
        `;
        editItem.addEventListener('click', () => {
            menu.remove();
            // Priorizar LaTeX para edición (más fácil de editar que MathML)
            const latexValue = extractLatexFromElement(formulaElement);
            const mathmlValue = extractMathMLFromElement(formulaElement);
            const stylesValue = extractStylesFromElement(formulaElement);
            onEdit({ latex: latexValue, mathml: mathmlValue, element: formulaElement, styles: stylesValue });
        });
        menu.appendChild(editItem);
    }
    
    // Posicionar menú
    menu.style.left = `${event.clientX}px`;
    menu.style.top = `${event.clientY}px`;
    document.body.appendChild(menu);
    
    // Ajustar si se sale de la pantalla
    const rect = menu.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
        menu.style.left = `${window.innerWidth - rect.width - 10}px`;
    }
    if (rect.bottom > window.innerHeight) {
        menu.style.top = `${window.innerHeight - rect.height - 10}px`;
    }
    
    // Cerrar al hacer clic fuera
    const closeMenu = (e) => {
        if (!menu.contains(e.target)) {
            menu.remove();
            document.removeEventListener('click', closeMenu);
            document.removeEventListener('contextmenu', closeMenu);
        }
    };
    setTimeout(() => {
        document.addEventListener('click', closeMenu);
        document.addEventListener('contextmenu', closeMenu);
    }, 0);
}

/**
 * Inicializa el menú contextual para fórmulas en un contenedor
 * @param {HTMLElement} container - Contenedor donde buscar fórmulas (ej: editor)
 * @param {Function} onEdit - Callback para editar fórmulas (opcional)
 * @returns {Function} - Función para remover los event listeners
 */
export function initFormulaContextMenu(container, onEdit = null) {
    if (!container) return () => {};
    
    const handleContextMenu = (event) => {
        // Buscar si el clic fue en una fórmula o dentro de un widget de fórmula
        const formula = event.target.closest('img[data-mathml], .nimbus-formula, .Wirisformula');
        // nimbus-division tiene su propio menú contextual — no interceptar aquí
        if (formula && !formula.classList.contains('nimbus-division')) {
            showFormulaContextMenu(event, formula, onEdit);
        }
    };
    
    container.addEventListener('contextmenu', handleContextMenu);
    
    // Retornar función de limpieza
    return () => {
        container.removeEventListener('contextmenu', handleContextMenu);
    };
}

/**
 * Comprime el HTML de MathJax eliminando los SVGs pesados y manteniendo solo el MathML
 * Esto reduce el tamaño del contenido en ~70% (de ~16KB a ~5KB por ejercicio)
 * 
 * Estructura original (pesada):
 * <mjx-container class="MathJax" jax="SVG">
 *   <svg>...</svg>  <!-- ~5KB de paths SVG - SE ELIMINA -->
 *   <mjx-assistive-mml>
 *     <math data-original-latex="...">...</math>  <!-- ~1KB - SE MANTIENE -->
 *   </mjx-assistive-mml>
 * </mjx-container>
 * 
 * Estructura comprimida (ligera):
 * <span class="nimbus-formula-wrapper" style="display:inline;">
 *   <math data-original-latex="...">...</math>
 * </span>
 * 
 * MathJax re-renderizará el MathML automáticamente al cargar
 * 
 * @param {string} html - HTML con mjx-container de MathJax
 * @returns {string} - HTML comprimido con solo MathML
 */
export function comprimirMathJaxParaGuardado(html) {
    if (!html || typeof html !== 'string') return html;
    
    // Si no hay mjx-container, no hay nada que comprimir
    if (!html.includes('mjx-container')) return html;
    
    let result = html;
    
    // Regex para encontrar mjx-container completos
    // Captura todo el contenido entre <mjx-container...> y </mjx-container>
    const mjxContainerRegex = /<mjx-container[^>]*>[\s\S]*?<\/mjx-container>/gi;
    
    result = result.replace(mjxContainerRegex, (match) => {
        try {
            // Buscar el <math> dentro de mjx-assistive-mml
            const mathMatch = match.match(/<math[^>]*>[\s\S]*?<\/math>/i);
            
            if (mathMatch && mathMatch[0]) {
                const mathML = mathMatch[0];
                // Envolver en span con clase para que MathJax lo re-renderice
                return `<span class="nimbus-formula-wrapper" style="display:inline;">${mathML}</span>`;
            }
            
            // Si no encontramos <math>, intentar extraer data-original-latex del SVG
            const latexMatch = match.match(/data-original-latex=["']([^"']+)["']/i);
            if (latexMatch && latexMatch[1]) {
                try {
                    // Decodificar el LaTeX (está en base64)
                    const latex = decodeURIComponent(escape(atob(latexMatch[1])));
                    // Convertir a MathML
                    const mathml = mathliveConvert(latex);
                    if (mathml && mathml.includes('<math')) {
                        // Inyectar data-original-latex en el math tag
                        const mathmlWithLatex = mathml.replace(/<math/, `<math data-original-latex="${latexMatch[1]}"`);
                        return `<span class="nimbus-formula-wrapper" style="display:inline;">${mathmlWithLatex}</span>`;
                    }
                } catch (e) {
                    console.warn('[comprimirMathJaxParaGuardado] Error decodificando LaTeX:', e);
                }
            }
            
            // Si no pudimos extraer nada útil, mantener el original
            return match;
        } catch (e) {
            console.warn('[comprimirMathJaxParaGuardado] Error procesando mjx-container:', e);
            return match;
        }
    });
    
    return result;
}

// ─── NimbusDivision: compresión y expansión ───────────────────────────────────

/**
 * Al GUARDAR: reemplaza cada <img class="nimbus-division"> (SVG pesado)
 * por un <span class="nimbus-division-data"> con solo el JSON de datos (~100 bytes).
 */
export function comprimirDivisionParaGuardado(html) {
    if (!html || typeof html !== 'string') return html;
    if (!html.includes('nimbus-division')) return html;

    // Span widget de CKEditor con data-division
    const spanRegex = /<span[^>]*class="[^"]*nimbus-division[^"]*"[^>]*data-division="([^"]*)"[^>]*>[\s\S]*?<\/span>/gi;
    let result = html.replace(spanRegex, (match, encoded) => {
        if (!encoded) return match;
        return `<span class="nimbus-division-data" data-division="${encoded}"></span>`;
    });

    // Fallback: img.nimbus-division suelto
    const imgRegex = /<img[^>]*class="[^"]*nimbus-division[^"]*"[^>]*data-division="([^"]*)"[^>]*>/gi;
    result = result.replace(imgRegex, (match, encoded) => {
        if (!encoded) return match;
        return `<span class="nimbus-division-data" data-division="${encoded}"></span>`;
    });

    return result;
}

/**
 * Al MOSTRAR: reemplaza cada <span class="nimbus-division-data"> por el SVG
 * generado en vivo. Usado en MathContent y al cargar en CKEditor.
 */
export function expandirDivisionDesdeGuardado(html) {
    if (!html || typeof html !== 'string') return html;
    if (!html.includes('nimbus-division-data')) return html;

    let divisionToSvg, divisionSoloCajonToSvg, calcularDivision;
    try {
        const mod = require('../NimbusDivision/divisionToSvg');
        divisionToSvg = mod.divisionToSvg;
        divisionSoloCajonToSvg = mod.divisionSoloCajonToSvg;
        calcularDivision = require('../NimbusDivision/divisionUtils').calcularDivision;
    } catch (e) {
        console.warn('[expandirDivision] imports fallaron:', e);
        return html;
    }

    return html.replace(
        /<span[^>]*class="nimbus-division-data"[^>]*data-division="([^"]*)"[^>]*><\/span>/gi,
        (match, encoded) => {
            try {
                const divisionData = JSON.parse(decodeURIComponent(escape(atob(encoded))));
                let result;
                if (divisionData.soloCajon) {
                    result = divisionSoloCajonToSvg(divisionData.dividendo, divisionData.divisor);
                } else {
                    const data = calcularDivision(divisionData.dividendo, divisionData.divisor);
                    if (!data) return match;
                    // calcularDivision() reconstruye la división SOLO a partir de los
                    // números, así que devuelve un objeto sin las opciones de
                    // visualización. divisionToSvg() las lee de este mismo objeto
                    // (simplificada, sinDecimales y operacionOriginal en su cabecera;
                    // sinMenos al pintar las restas), de modo que pasarle `data` a
                    // secas regeneraba el SVG con la división completa: decimales,
                    // todos los pasos y símbolos de resta. El data-division sí las
                    // conservaba —por eso al reabrir el modal salían marcadas—, pero
                    // la imagen guardada ya no las respetaba.
                    result = divisionToSvg({
                        ...data,
                        simplificada: !!divisionData.simplificada,
                        sinDecimales: !!divisionData.sinDecimales,
                        sinMenos: !!divisionData.sinMenos,
                        operacionOriginal: !!divisionData.operacionOriginal,
                    });
                }
                if (!result) return match;
                return `<span class="nimbus-formula-wrapper" style="display:inline;"><img class="nimbus-formula nimbus-division" src="${result.svgDataUrl}" data-division="${encoded}" alt="División ${divisionData.dividendo} ÷ ${divisionData.divisor}" role="math" align="middle" width="${result.width}" height="${result.height}" style="max-width:none;vertical-align:middle;"></span>`;
            } catch (e) {
                console.warn('[expandirDivision] error:', e);
                return match;
            }
        }
    );
}
